import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * PUBLIC customer-satisfaction survey. This page is mounted OUTSIDE the MSAL auth
 * gate — the opaque token in the URL is the only authorization, so we never touch
 * the authenticated api client. Everything goes through plain window.fetch.
 */

interface CsatSurvey {
  token: string;
  ticketNumber: number;
  subject: string;
  responded: boolean;
  rating: number | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'notFound' }
  | { kind: 'ready'; survey: CsatSurvey };

function Shell({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>{children}</CardContent>
      </Card>
    </Box>
  );
}

export function CsatPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'notFound' });
      return;
    }
    let active = true;
    void (async () => {
      try {
        const res = await window.fetch(`/api/csat/${token}`);
        if (!active) return;
        if (!res.ok) {
          setState({ kind: 'notFound' });
          return;
        }
        const survey = (await res.json()) as CsatSurvey;
        if (active) setState({ kind: 'ready', survey });
      } catch {
        if (active) setState({ kind: 'notFound' });
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  async function submit() {
    if (!token || rating === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await window.fetch(`/api/csat/${token}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment.trim() || undefined }),
      });
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      if (res.status === 409) {
        // Already answered elsewhere — treat as a (gracefully) completed survey.
        setSubmitted(true);
        return;
      }
      setSubmitError('Sorry, we could not record your feedback. Please try again.');
    } catch {
      setSubmitError('Sorry, we could not record your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <Shell>
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Shell>
    );
  }

  if (state.kind === 'notFound') {
    return (
      <Shell>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Survey unavailable
        </Typography>
        <Typography color="text.secondary">
          This satisfaction survey link is invalid or has expired. If you have already
          submitted feedback, thank you — there is nothing more to do.
        </Typography>
      </Shell>
    );
  }

  const { survey } = state;

  // Thank-you state — either just submitted, or the survey was already answered.
  if (submitted || survey.responded) {
    return (
      <Shell>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Thank you!
        </Typography>
        <Typography color="text.secondary">
          Your feedback on ticket #{survey.ticketNumber} has been recorded. We appreciate
          you taking the time to let us know how we did.
        </Typography>
      </Shell>
    );
  }

  return (
    <Shell>
      <Typography variant="overline" color="text.secondary">
        Ticket #{survey.ticketNumber}
      </Typography>
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        How did we do?
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {survey.subject}
      </Typography>

      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Your rating
          </Typography>
          <Rating
            name="csat-rating"
            size="large"
            value={rating}
            onChange={(_e, value) => setRating(value)}
          />
        </Box>

        <TextField
          label="Anything you'd like to add? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Box sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={submitting || rating === null}
          >
            Submit feedback
          </Button>
        </Box>
      </Stack>
    </Shell>
  );
}
